/** @module api/configure */

import { isEmpty } from 'lodash'
import config from '@/config/api.json'

/**
 * Supported options for building the API URL and mapping those to current `window.location` object.
 */
export const API_URL_OPTS = {
  protocol: 'protocol',
  host: 'hostname',
  port: 'port',
  path: 'pathname'
}

/**
 * Builds the URL for API requests.
 * Will take the config from `config/api.json`.
 * Passed options during function call will overwrite values specified in the config file.
 * Missing options will be replaced by the default auto-determined fallback.
 * Thereto, the `window.location` object is used.
 *
 * @function
 * @see [API_URL_OPTS]{@link module:api/configure.API_URL_OPTS}
 *
 * @param {object} opts allows to overwrite the configured and default options. For supported one see [API_URL_OPTS]{@link module:api/configure.API_URL_OPTS}.
 * @param {string} env (optional) defines the env which is used during parsing the grpc config file.
 *
 * @return {string} the URL as specified [here]{@link https://en.wikipedia.org/wiki/URL}
 */
export function getApiUrl(opts = {}, env) {
  const url = {}
  if (!env) {
    env = process.env.NODE_ENV
  }

  Object.assign(url, config[env])
  Object.assign(url, opts)
  determineDefaults(url)

  if (url['path'] && !/^\/\w+/.test(url['path'])) {
    throw new Error(`Invalid path '${url['path']}'. Path has to be of form '/foo/bar/34'`)
  }

  // prettier-ignore
  return `${url.protocol}://${url.host}${url.port ? ':' + url.port : ''}${url.path}`
}

/**
 * Allows to add json:api `fields, filter, sort` query params to request payload
 *
 * @function
 *
 * @param {object} payload has to be the payload used for the API call
 * @param {string} resourceType has to be the type of the requested resource, e.g. `users` or `energy_management_systems`. C.f. json:api
 * @param {object} params
 * @param {array} params.fields (optional) is a list of resource fields (attributes) which are requested to be included in the response
 * @param {object} params.filter (optional) is a filter to by applied to the resource query, e.g. `{ id: '1,2,34' }`
 * @param {string} params.sort (optional) is a sort-string (resource attribute/field name) the returned collection should be sorted by
 * @param {array} params.include (optional) is a list of relations to be included (if releations of an relation have to be included, seperate those by an dot, e.g. `foos.bars`.)
 *
 * @return {object} the modified payload
 */
export function addQueryParamsToReqPayload(
  payload,
  resourceType,
  { fields = [], filter = {}, sort = '', include = [] }
) {
  if (fields.length) {
    payload['fields'] = {
      [resourceType]: fields.join(',')
    }
  }

  if (include.length) {
    payload['include'] = include.join(',')
  }

  if (!isEmpty(filter)) {
    payload['filter'] = {
      [resourceType]: filter
    }
  }

  if (sort) {
    payload['sort'] = sort
  }

  return payload
}

// side-effect: updates `urlOpts`
function determineDefaults(urlOpts) {
  for (const [k, m] of Object.entries(API_URL_OPTS)) {
    if (urlOpts[k] == null) {
      let opt = window.location[m]
      if (k === 'protocol' && /\w+:$/.test(opt)) {
        opt = opt.match(/(\w+):$/)[1]
      }
      // add missing preceding `/`
      if (k === 'path' && /^\w+.*/.test(opt)) {
        opt = '/' + opt
      }
      // rm trailing `/`
      if (k === 'path' && /.*\/$/) {
        opt = ''
      }
      urlOpts[k] = opt
    }
  }

  return urlOpts
}
